import { CommonModule } from '@angular/common';
import {
  Component, computed, effect, forwardRef, input, model, signal,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { FieldState, isValidEmail } from '../../../models/field.model';
import { InlineSvgComponent } from '../../inline-svg/inline-svg.component';

@Component({
  selector: 'ui-email-field',
  imports: [ CommonModule, FormsModule, InlineSvgComponent ],
  templateUrl: './email-field.component.html',
  styleUrl: './email-field.component.scss',
  providers: [ {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => EmailFieldComponent),
    multi: true,
  } ],
})
export class EmailFieldComponent implements ControlValueAccessor {
  /** Defines the style of the email field. */
  fieldState = FieldState;
  /** A unique name identifier for the email component.
   *  The name is a string generated with a random hexadecimal number.
  */
  name = input<string>(`ui-email-field-${(Math.round((Math.random() * 100000000)).toString(16))}`);
  /** Output bind value. */
  value = model<string | undefined>('');
  /** Defines the current style of the email field. */
  state = input<FieldState>(FieldState.Default);
  /** Defines the placeholder  of the email field. */
  placeholder = input<string>('');
  /** Alert text message to be displayed based on the value. */
  alert = input<string>();
  /** Renders the field read-only, for example while the contract it belongs to is locked. */
  disabled = input<boolean>(false);

  currentState = computed(() => {
    if (this.state() !== FieldState.Default) {
      return this.state();
    }

    const value = this.value() ?? '';
    const invalid = this.touched() && value.length > 0 && !isValidEmail(value);

    return invalid ? FieldState.Invalid : FieldState.Default;
  });

  /** Computed property that returns the icon
   * that should be displayed based on the FieldState.
  */
  icon = computed(() => {
    switch (this.currentState()) {
      case FieldState.Success:
      case FieldState.Info:
        return 'success';
      case FieldState.Invalid:
        return 'error';
      default:
        return '';
    }
  });

  private touched = signal<boolean>(false);

  constructor() {
    effect(() => {
      this.onChange(this.value() ?? '');
    });
  }

  onBlur(): void {
    this.touched.set(true);
    this.value.set(this.value()?.trim() ?? '');
    this.onTouched();
  }

  writeValue(val: string): void {
    this.value.set(val ?? '');
    this.touched.set(false);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};
}
